/* eslint-disable @typescript-eslint/no-explicit-any */

import { useAuth, useCart, useStore } from '@/containers'
import type { Province } from '@/types'
import { nonAccentVietnamese } from '@/utils/converter'
import { getSaleRegion } from '@/utils/endpoint'
import { Switch } from '@headlessui/react'
import { getCookie, setCookie } from 'cookies-next'
import { useCallback, useEffect, useRef, useState } from 'react'
import { SearchInput } from '../SearchInput'
import PopupBody from './PopupBody'
import PopupFrame from './PopupFrame'
import PopupTitle from './PopupTitle'

const ModalChooseLocation = () => {
  const { setRefetch } = useAuth()
  const { guestInfo, setGuestInfo } = useCart()
  const {
    regions,
    setSaleRegion,
    isOpenPopupLocation,
    setIsOpenPopupLocation,
    areaType,
    setAreaType,
  } = useStore()

  const [localAreaType, setLocalAreaType] = useState<'old' | 'new'>(
    (getCookie('area-type') as 'old' | 'new') || areaType || 'old'
  )
  const provinceCode = getCookie('province') as string | undefined

  // Lưu giá trị gốc khi popup mở — dùng để revert nếu đóng mà không chọn
  const originalAreaTypeRef = useRef<'old' | 'new'>('old')

  // Danh sách tỉnh hiển thị trong modal
  const [displayRegions, setDisplayRegions] = useState<Province[]>(
    (regions as unknown as Province[]) || []
  )
  const [isLoadingRegions, setIsLoadingRegions] = useState(false)

  /** Fetch danh sách tỉnh theo areaType */
  const fetchRegionsByType = useCallback(async (type: 'old' | 'new') => {
    setIsLoadingRegions(true)
    const data = await getSaleRegion(type)
    if (data) setDisplayRegions(data)
    setIsLoadingRegions(false)
  }, [])

  // Khi popup mở: lưu giá trị gốc, sync localAreaType từ cookie và load regions
  useEffect(() => {
    if (isOpenPopupLocation) {
      const cookieAreaType = getCookie('area-type') as 'old' | 'new' | undefined
      const resolvedType = cookieAreaType || areaType || 'old'
      originalAreaTypeRef.current = resolvedType
      setLocalAreaType(resolvedType)
      fetchRegionsByType(resolvedType)
    }
  }, [isOpenPopupLocation, areaType, fetchRegionsByType])

  /** Toggle switch — chỉ thay đổi state local, KHÔNG ghi cookie */
  const handleAreaTypeChange = async (checked: boolean) => {
    const newType = checked ? 'new' : 'old'
    setLocalAreaType(newType)
    // Fetch lại danh sách tỉnh theo areaType mới (chỉ preview, chưa lưu)
    await fetchRegionsByType(newType)
  }

  /** Đóng popup — revert areaType về giá trị gốc */
  const handleClose = () => {
    const original = originalAreaTypeRef.current
    // Revert local state + store state về giá trị trước khi mở
    setLocalAreaType(original)
    setAreaType(original)
    setIsOpenPopupLocation(false)
    setLocation('')
    localStorage?.setItem('area-popup-seen', 'true')
  }

  const [location, setLocation] = useState('')

  return (
    <PopupFrame
      open={isOpenPopupLocation}
      onClose={handleClose}
      type='choose_location'
      className='w-full bg-white-400'
    >
      <PopupTitle className='px-1'>
        {
          <div className='flex flex-wrap'>
            <p className='mb-3 pt-1 text-fieldLabel font-bold'>
              Chọn Tỉnh/Thành phố
            </p>
            <div className='mb-2 mt-1 flex w-full items-center gap-x-3'>
              <Switch
                checked={localAreaType === 'new'}
                onChange={handleAreaTypeChange}
                className={`${
                  localAreaType === 'new' ? 'bg-primary-base' : 'bg-[#BFBFBF]'
                } relative inline-flex w-9 cursor-pointer items-center rounded-full py-[2px] transition`}
              >
                <span className='sr-only'>Đơn vị hành chính mới</span>
                <span
                  className={`${
                    localAreaType === 'new'
                      ? 'translate-x-[18px]'
                      : 'translate-x-[3px]'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                />
              </Switch>
              <div className='flex flex-col'>
                <span className='cursor-pointer text-sm font-medium text-dark-400'>
                  Sử dụng đơn vị hành chính mới
                </span>
              </div>
            </div>
            <SearchInput
              placeholder='Tìm nhanh tỉnh/thành phố'
              className='mb-3'
              value={location}
              onChange={e => setLocation(e.target?.value)}
            />
          </div>
        }
      </PopupTitle>
      <PopupBody className='px-0'>
        <div className='listLocation mt-1 h-[40vh] max-h-[40vh] w-full flex-wrap overflow-y-scroll pb-1'>
          {isLoadingRegions ? (
            <div className='flex w-full items-center justify-center py-8'>
              <span className='text-sm text-dark-400'>Đang tải...</span>
            </div>
          ) : (
            displayRegions
              ?.sort((a, b) => (a.order > b.order ? -1 : 1))
              .filter(store =>
                nonAccentVietnamese(store.name).includes(
                  nonAccentVietnamese(location)
                )
              )
              .map((item, index) => (
                <button
                  key={item.code || index}
                  className={`${
                    provinceCode === item.code && 'text-bold'
                  } box-content block h-10 w-full overflow-hidden border-b border-gray outline-none`}
                  onClick={async () => {
                    const areaCode = item.parentCode

                    setRefetch(true)
                    setIsOpenPopupLocation(false)
                    setLocation('')

                    await setSaleRegion(areaCode)
                    await localStorage?.setItem('area-popup-seen', 'true')
                    localStorage.setItem('Area-Code', areaCode)
                    setCookie('Area-Code', areaCode, {
                      maxAge: 2147483647,
                    })

                    // Lưu province = tỉnh đã chọn
                    localStorage.setItem('province', item.code)
                    setCookie('province', item.code, {
                      maxAge: 2147483647,
                    })

                    // CHỈ lưu area-type khi thực sự chọn tỉnh
                    localStorage.setItem('area-type', localAreaType)
                    setCookie('area-type', localAreaType, {
                      maxAge: 2147483647,
                    })

                    setRefetch(false)
                    setAreaType(localAreaType)
                    setGuestInfo({
                      ...guestInfo,
                      provinceCode: item.code,
                      districtCode: '',
                      wardCode: '',
                      address: '',
                      saleRegionCode: areaCode,
                      areaType: localAreaType,
                    } as any)

                    if (window.location.search.includes('shippingAddress')) {
                      const url = new URL(window.location.href)
                      url.searchParams.delete('shippingAddress')
                      url.searchParams.delete('pis')
                      window.history.pushState({}, '', url.toString())
                    }
                    window.location.reload()
                  }}
                >
                  <div className='flex items-center'>
                    <div className='flex flex-1 flex-row flex-wrap items-start justify-start gap-[2px] text-center'>
                      <p
                        className={` block w-full text-mainBody text-dark-500 ${
                          provinceCode === item.code &&
                          'font-medium text-primary-base'
                        }`}
                      >
                        {item.name}
                      </p>
                    </div>
                  </div>
                </button>
              ))
          )}
        </div>
      </PopupBody>
    </PopupFrame>
  )
}

export default ModalChooseLocation
